import { useQuery } from "@tanstack/react-query";
import { getCompany } from "@/lib/api/clients/CompanyClient";
import { getEventEntitlements } from "@/lib/api/clients/EventClient";
import { useAuth } from "@/lib/auth/AuthContext";
import { AI_CREDITS_KEY_PREFIX } from "@/lib/ai/creditLedger";
import { qk } from "@/lib/query/queryKeys";
import { useAiAssistantOptional } from "@/providers/AiAssistantProvider";
import type { Company } from "@/types/Company";
import type { EventEntitlements, EventEntitlementsAiCredits } from "@/types/EventEntitlements";

/** Mirror of the server-side legacy default applied when a company doc
 *  carries no aiTurnQuota (main-backend DEFAULT_AI_TURN_QUOTA). */
const LEGACY_DEFAULT_QUOTA = 2000;

/** Remaining-share threshold below which the UI shows the low-credits pill. */
const LOW_THRESHOLD = 0.2;

export interface AiCreditsBalance {
  /** Undefined until loaded (or unauthenticated) — render nothing then. */
  loaded: boolean;
  used: number;
  /** Effective cap = plan quota (or legacy default) + purchased balance. */
  cap: number;
  remaining: number;
  /** remaining/cap <= 20% — the pill trigger. */
  low: boolean;
  exhausted: boolean;
  /**
   * ISO instant of the next reset, or null when nothing is scheduled.
   *
   * null is the honest answer for a lifetime allowance (FREE), and it must
   * stay null rather than falling back to a subscription date — see
   * Company.monthlyQuotaResetAt.
   */
  resetAt: string | null;
  /**
   * Whether this plan renews its quota at all — tri-state by presence.
   *
   * false is the only value that licenses the negative sentence; undefined
   * means "not classified yet" and must stay silent. See Company.quotaRenews.
   */
  quotaRenews: boolean | undefined;
}

const UNLOADED: AiCreditsBalance = {
  loaded: false,
  used: 0,
  cap: 0,
  remaining: 0,
  low: false,
  exhausted: false,
  resetAt: null,
  quotaRenews: undefined,
};

function fromUsedAndCap(
  used: number,
  cap: number,
  resetAt: string | null,
  quotaRenews: boolean | undefined,
): AiCreditsBalance {
  const remaining = Math.max(0, cap - used);
  return {
    loaded: true,
    used,
    cap,
    remaining,
    low: cap > 0 && remaining / cap <= LOW_THRESHOLD,
    exhausted: remaining <= 0,
    resetAt,
    // Passed through undefined-preserving on purpose: `?? false` would turn
    // "not classified yet" into "does not renew" and put a false sentence in
    // front of every paying customer the pipeline has not reached.
    quotaRenews,
  };
}

/**
 * Velo AI-credit balance for the drawer/wizard low-credits indicator.
 *
 * WHICH company: a turn is debited from the company that OWNS the event Velo
 * is scoped to (`AiAgentService` bills `event.companyId`), so in event scope
 * the balance comes off the event's entitlements read — the SAME query the
 * sender wall / website gates use, selected down to `aiCredits`. The login
 * company's balance governs nothing there, and its read is refused for a
 * same-company EDITOR/VIEWER. Company scope (no event) keeps the company read.
 *
 * Company leg deliberately NOT via useCompany: qk.company.current's cache key
 * does not include the fields CSV, so fetching a narrow AI-only projection
 * through it would poison the shared company cache for consumers expecting
 * the default fields. Own key, invalidated by the purchase page's ["company"]
 * prefix invalidation.
 */
export function useAiCreditsBalance(): AiCreditsBalance {
  const { state } = useAuth();
  const companyId = state.kind === "authenticated" ? state.companyId : null;
  const scopeEventId = useAiAssistantOptional()?.scopeEventId ?? null;

  // Both queries every render (rules-of-hooks); the scope decides which fires.
  const eventQ = useQuery({
    queryKey: scopeEventId ? qk.events.entitlements(scopeEventId) : ["events", "entitlements", "none"],
    queryFn: () => getEventEntitlements(scopeEventId ?? ""),
    // `select` keeps one cache entry per event for every consumer of the read;
    // the section is absent for a role without ai:use — "not loaded", not 0.
    select: (d: EventEntitlements): EventEntitlementsAiCredits | null => d?.aiCredits ?? null,
    enabled: !!scopeEventId,
    staleTime: 30_000,
  });

  const q = useQuery({
    // Built from the shared prefix, never re-typed: the invalidation helper
    // matches on that prefix, and two independent literals is exactly how the
    // ring came to be un-refreshable in the first place. Renaming the constant
    // now moves both sides together.
    queryKey: [...AI_CREDITS_KEY_PREFIX, companyId ?? "none"],
    // One CSV for balance AND reset date. Never split the date into its own
    // request: sanitizeFields returns null when every requested field is
    // unknown, and the backend then fails the whole call — so a lone
    // monthlyQuotaResetAt against an older backend would break the drawer
    // instead of degrading to "no date".
    queryFn: async () => {
      const res = await getCompany(
        companyId!,
        "aiTurnsUsed,aiTurnQuota,purchasedAiTurns,monthlyQuotaResetAt,quotaRenews",
      );
      // The API layer RETURNS a ServerError object instead of throwing, so
      // react-query would treat a 403/5xx as a successful empty result: no
      // error state, no retry, and the balance stuck at "still loading"
      // forever — the ring silently disappears and the user loses the
      // forewarning entirely. Throwing hands the failure to the retry policy.
      if (res && typeof res === "object" && "isServerError" in res) {
        throw new Error("aiCreditsUnavailable");
      }
      return res;
    },
    enabled: !!companyId && !scopeEventId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (scopeEventId) {
    const credits = eventQ.data;
    // No section (no ai:use) or a body without the cap: "not loaded". A
    // missing cap must never be read as 0 — that is "exhausted", a claim
    // about billing built on a number nobody sent.
    if (credits == null || credits.cap == null || !Number.isFinite(credits.cap)) return UNLOADED;
    // `cap` is already quota + purchased, computed server-side: the split is
    // the owning company's business, not an outside collaborator's.
    return fromUsedAndCap(
      credits.used != null && Number.isFinite(credits.used) ? credits.used : 0,
      credits.cap,
      // Explicit null or a dropped key — both mean "no reset scheduled".
      credits.monthlyQuotaResetAt ?? null,
      credits.quotaRenews,
    );
  }

  const data = q.data as Company | undefined;
  if (!data || typeof data !== "object" || "isServerError" in data) return UNLOADED;

  const used = data.aiTurnsUsed ?? 0;
  const cap = (data.aiTurnQuota ?? LEGACY_DEFAULT_QUOTA) + (data.purchasedAiTurns ?? 0);
  return fromUsedAndCap(used, cap, data.monthlyQuotaResetAt ?? null, data.quotaRenews);
}
